import { useRouter, useSearch } from "@tanstack/react-router";
import { useClient, useFamily } from "creso-core/hooks";
import JSZip from "jszip";
import { useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  V2Loader,
  VisuallyHidden,
} from "web-design-system";
import { ChangeOfBrokerForm, type ChangeOfBrokerFamily } from "web-core";
import { Route } from "@/routes/__root";
import ChooseClient from "@crm/components/search/choose-client";

const createZip = () => new JSZip();

/**
 * Partners change of broker dialog. The body follows the URL: a `family_id`
 * shows the family form, a `client_id` the client form, and with neither the
 * partner first chooses a client.
 */
export function ChangeOfBrokerDialog() {
  const router = useRouter();
  const { client_id: clientId, family_id: familyId } = useSearch({
    from: Route.id,
  });

  const { data: client } = useClient(clientId ?? 0, !familyId && !!clientId);
  const { data: familyDetail } = useFamily(familyId ?? 0, !!familyId);

  const family = useMemo((): ChangeOfBrokerFamily | undefined => {
    if (!familyDetail) return undefined;

    return {
      name: familyDetail.name,
      members: (familyDetail.members ?? []).flatMap((member) =>
        member.investment_account_id && member.client_id
          ? [
              {
                clientId: member.client_id,
                name: member.display_name ?? member.name,
                investmentAccount: member.investment_account_id,
              },
            ]
          : []
      ),
    };
  }, [familyDetail]);

  const onOpenChange = (open: boolean) => {
    if (!open) router.history.back();
  };

  const onImportHoldings = () =>
    router.navigate({
      to: ".",
      search: (prev) => ({ ...prev, modal: "import-holdings" }),
    });

  const renderContent = () => {
    if (familyId) {
      return family ? (
        <ChangeOfBrokerForm
          family={family}
          createZip={createZip}
          onImportHoldings={onImportHoldings}
        />
      ) : (
        <V2Loader isLoading className="w-250" />
      );
    }

    if (clientId) {
      return (
        <ChangeOfBrokerForm
          client={client}
          onImportHoldings={onImportHoldings}
        />
      );
    }

    return (
      <ChooseClient
        onSelect={(selected) =>
          router.navigate({
            to: ".",
            search: (prev) => ({ ...prev, client_id: selected.id }),
            replace: true,
          })
        }
      />
    );
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="p-0 max-w-250 w-auto h-150">
        <VisuallyHidden>
          <DialogTitle>Change of broker</DialogTitle>
          <DialogDescription>Change of broker dialog</DialogDescription>
        </VisuallyHidden>
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
}
